import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, TrendingUp, Users, Award, Clock, ArrowRight } from "lucide-react";
import { track } from "@/lib/analytics";

const PROGRAM_TYPES = [
  { value: "basico", label: "Formación básica" },
  { value: "diplomado", label: "Diplomado ejecutivo" },
  { value: "custom", label: "Programa custom" },
];
const HOURS = [20, 40, 80, 120];

function fmtMxn(n) {
  return "$" + (n || 0).toLocaleString("es-MX") + " MXN";
}

export default function RoiSimulator() {
  const [volume, setVolume] = useState(200);
  const [programType, setProgramType] = useState("diplomado");
  const [hours, setHours] = useState(40);
  const [estimate, setEstimate] = useState(null);
  const [showLead, setShowLead] = useState(false);
  const [breakdown, setBreakdown] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams({ volume, program_type: programType, hours });
    api.get(`/public/roi-simulator/estimate?${params.toString()}`)
      .then((r) => setEstimate(r.data))
      .catch(() => setEstimate(null));
  }, [volume, programType, hours]);

  const range = useMemo(() => estimate ? `${fmtMxn(estimate.range_low_mxn)} — ${fmtMxn(estimate.range_high_mxn)}` : "—", [estimate]);

  return (
    <section className="mt-16 p-6 lg:p-8 rounded-2xl border border-gray-200 bg-[#F9FAFB]" data-testid="roi-simulator">
      <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Simulador ROI</p>
      <h2 className="mt-2 text-3xl font-bold text-[#0B0B0B] font-display">Calcula el impacto para tu institución</h2>
      <p className="mt-2 text-gray-600 max-w-2xl">Ajusta los parámetros y obtén una estimación anual. El desglose exacto se libera al agendar demo.</p>

      <div className="mt-8 grid lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">
              Cantidad de PYMEs cliente a beneficiar
            </span>
            <input
              type="range" min={50} max={5000} step={50}
              value={volume} onChange={(e) => setVolume(parseInt(e.target.value, 10))}
              data-testid="roi-volume-slider"
              className="w-full mt-2 accent-[#F26A21]"
              aria-label="Cantidad de PYMEs cliente"
            />
            <p className="mt-1 font-mono text-2xl font-bold text-[#0B0B0B]" data-testid="roi-volume-value">
              {volume.toLocaleString("es-MX")}
            </p>
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">Tipo de programa</span>
            <select
              value={programType} onChange={(e) => setProgramType(e.target.value)}
              data-testid="roi-program-type"
              className="w-full mt-2 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]"
            >
              {PROGRAM_TYPES.map((p) => (<option key={p.value} value={p.value}>{p.label}</option>))}
            </select>
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">Duración promedio</span>
            <div className="mt-2 flex gap-2 flex-wrap">
              {HOURS.map((h) => (
                <button
                  key={h} type="button"
                  onClick={() => setHours(h)}
                  data-testid={`roi-hours-${h}`}
                  aria-pressed={hours === h}
                  className={`px-3 py-1.5 rounded border text-sm font-semibold transition-colors ${
                    hours === h
                      ? "bg-[#0B0B0B] border-[#0B0B0B] text-white"
                      : "bg-white border-gray-300 text-[#0B0B0B] hover:border-[#F26A21]"
                  }`}
                >
                  {h}h
                </button>
              ))}
            </div>
          </label>
        </div>

        <div className="space-y-4" data-testid="roi-output">
          <div className="p-5 bg-white border border-gray-200 rounded-xl">
            <p className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">Rango estimado anual</p>
            <p className="mt-2 font-mono text-2xl font-bold text-[#0B0B0B]" data-testid="roi-range">{range}</p>
            <p className="mt-1 text-xs text-gray-500">Banda referencial · precio exacto disponible tras demo.</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Stat icon={Users} label="Activos año 1" value={estimate?.activated_year1?.toLocaleString("es-MX")} sub={`${estimate?.activation_rate_pct || 70}% activation`} testid="roi-activated" />
            <Stat icon={Clock} label="Horas totales" value={estimate?.hours_total?.toLocaleString("es-MX")} sub="Formación LATAM" testid="roi-hours-total" />
            <Stat icon={Award} label="Certificados" value={estimate?.certificates_expected?.toLocaleString("es-MX")} sub="Estimado 55%" testid="roi-certs" />
          </div>
          <button
            type="button"
            onClick={() => { track("roi_simulator_cta"); setShowLead(true); }}
            data-testid="roi-cta"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm"
          >
            <TrendingUp className="w-4 h-4" /> Ver desglose detallado y agendar demo <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showLead && (
        <LeadModal
          volume={volume} programType={programType} hours={hours}
          onClose={() => setShowLead(false)}
          onDone={(b) => { setBreakdown(b); setShowLead(false); }}
        />
      )}
      {breakdown && <BreakdownModal breakdown={breakdown} onClose={() => setBreakdown(null)} />}
    </section>
  );
}

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="p-3 bg-white border border-gray-200 rounded-lg" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
        <Icon className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
      </div>
      <p className="mt-1 font-mono text-lg font-bold text-[#0B0B0B]">{value ?? "—"}</p>
      <p className="text-[10px] text-gray-500">{sub}</p>
    </div>
  );
}

function LeadModal({ volume, programType, hours, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", role: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/roi-simulator/lead", {
        ...form, volume, program_type: programType, hours,
      });
      track("roi_simulator_lead", { volume, program_type: programType, hours });
      toast.success("Solicitud enviada. Un asesor te contactará en 24 h.");
      onDone(data.breakdown);
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="roi-lead-modal"
        className="w-full max-w-md bg-white rounded-xl p-6 space-y-3"
      >
        <div className="flex justify-between items-start">
          <h3 className="text-xl font-bold text-[#0B0B0B]">Recibe el desglose exacto</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="text-gray-500"><X /></button>
        </div>
        <p className="text-xs text-gray-500">Un asesor DCC te contactará en 24 h para agendar demo.</p>
        <TextInput label="Nombre completo" val={form.name} on={(v) => setForm({ ...form, name: v })} testid="roi-lead-name" required />
        <TextInput label="Correo corporativo" type="email" val={form.email} on={(v) => setForm({ ...form, email: v })} testid="roi-lead-email" required />
        <TextInput label="Empresa" val={form.company} on={(v) => setForm({ ...form, company: v })} testid="roi-lead-company" required />
        <TextInput label="Teléfono" val={form.phone} on={(v) => setForm({ ...form, phone: v })} testid="roi-lead-phone" />
        <TextInput label="Cargo" val={form.role} on={(v) => setForm({ ...form, role: v })} testid="roi-lead-role" />
        <button type="submit" disabled={submitting} data-testid="roi-lead-submit"
          className="w-full py-2.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold disabled:opacity-60">
          {submitting ? "Enviando..." : "Enviar y ver desglose"}
        </button>
      </form>
    </div>
  );
}

function TextInput({ label, val, on, testid, type = "text", required }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">{label}{required && " *"}</span>
      <input type={type} required={required} value={val} onChange={(e) => on(e.target.value)}
        data-testid={testid}
        className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
    </label>
  );
}

function BreakdownModal({ breakdown, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="roi-breakdown-modal"
        className="w-full max-w-lg bg-white rounded-xl p-6 space-y-3">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-[#F26A21] font-semibold">Desglose exacto · Tier {breakdown.tier}</p>
            <h3 className="text-xl font-bold text-[#0B0B0B]">Propuesta anual</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="text-gray-500"><X /></button>
        </div>
        <Row k="Volumen" v={`${breakdown.volume.toLocaleString("es-MX")} licencias`} />
        <Row k="Tipo · Duración" v={`${breakdown.program_type} · ${breakdown.hours} h`} />
        <Row k="Precio unitario" v={fmtMxn(breakdown.unit_price_mxn)} />
        <Row k="Subtotal" v={fmtMxn(breakdown.subtotal_mxn)} />
        <Row k="IVA (16%)" v={fmtMxn(breakdown.tax_mxn)} />
        <div className="mt-2 pt-3 border-t border-gray-200">
          <Row k="TOTAL anual" v={<span className="font-bold text-[#F26A21] font-mono">{fmtMxn(breakdown.total_mxn)}</span>} highlight />
        </div>
        <div className="pt-3 mt-2 border-t border-gray-200 grid grid-cols-3 gap-2">
          <MiniStat label="Activos año 1" val={breakdown.activated_year1} />
          <MiniStat label="Horas totales" val={breakdown.hours_total.toLocaleString("es-MX")} />
          <MiniStat label="Certificados" val={breakdown.certificates_expected} />
        </div>
        <p className="text-xs text-gray-500 mt-3">Un asesor DCC te contactará en 24 h para agendar demo.</p>
      </div>
    </div>
  );
}

function Row({ k, v, highlight = false }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={`${highlight ? "text-[#0B0B0B] font-semibold" : "text-gray-600"}`}>{k}</span>
      <span className="text-[#0B0B0B] font-mono">{v}</span>
    </div>
  );
}

function MiniStat({ label, val }) {
  return (
    <div className="p-2 bg-[#F9FAFB] border border-gray-200 rounded">
      <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className="mt-0.5 font-mono text-sm font-bold text-[#0B0B0B]">{val}</p>
    </div>
  );
}
